/**
 * Pont entre le descriptif de domaine et le rendu React.
 *
 * `describeProNotification` (libs/domain/pro) ne renvoie que des CLÉS i18n, des
 * variables et des nombres bruts. C'est ici qu'on choisit une icône, une
 * palette, qu'on formate les montants en DT et qu'on traduit — la couche
 * domaine reste pure et testable sans React ni i18next.
 */

import type { ComponentType, CSSProperties } from 'react';
import type { TFunction } from 'i18next';
import {
  FileText,
  FileCheck2,
  FileX2,
  ShoppingBag,
  BadgeCheck,
  PackageCheck,
  Truck,
  PackageOpen,
  Undo2,
  Ban,
  Coins,
  TrendingDown,
  BarChart3,
  Users,
  PlayCircle,
  Sparkles,
  Inbox,
} from 'lucide-react';
import {
  describeProNotification,
  type ProNotificationIcon,
  type ProNotificationL10n,
  type ProNotificationPeriod,
  type ProNotificationStat,
  type ProNotificationTone,
  type ProNotificationView,
} from '@erp/domain/pro';
import type { ProNotification } from '@erp/data-access/interfaces';
import { formatTND } from './format';

// ------------------------------------------------------------------
// Palette par teinte
// ------------------------------------------------------------------

/** Les icônes lucide acceptent les props SVG : on garde `style` pour la teinte. */
export type NotificationIconComponent = ComponentType<{
  className?: string;
  style?: CSSProperties;
}>;

export interface ProNotificationPalette {
  /** Couleur de l'icône et du texte d'accent. */
  fg: string;
  /** Fond de la pastille d'icône. */
  bg: string;
}

/**
 * Chaque couple est un texte foncé sur son propre fond très clair : le
 * contraste passe l'AA même en 11 px, ce qui n'est pas le cas de
 * `--pro-primary` en petit corps (cf. la note sur `--pro-primary-text`).
 */
export const NOTIFICATION_PALETTE: Record<
  ProNotificationTone,
  ProNotificationPalette
> = {
  brand: { fg: '#B33F00', bg: '#FFEEE0' },
  success: { fg: '#0E7A45', bg: '#E6F7EF' },
  danger: { fg: '#B3132F', bg: '#FDE8EC' },
  money: { fg: '#8A5B00', bg: '#FEF3DC' },
  info: { fg: '#1D4ED8', bg: '#E7EFFE' },
  learning: { fg: '#5B3FD1', bg: '#EEEAFE' },
  neutral: { fg: '#666D80', bg: '#F1F2F4' },
};

const ICONS: Record<ProNotificationIcon, NotificationIconComponent> = {
  quote: FileText,
  'quote-check': FileCheck2,
  'quote-x': FileX2,
  order: ShoppingBag,
  'order-check': BadgeCheck,
  packed: PackageCheck,
  shipped: Truck,
  delivered: PackageOpen,
  returned: Undo2,
  rejected: Ban,
  'commission-in': Coins,
  'commission-out': TrendingDown,
  chart: BarChart3,
  users: Users,
  video: PlayCircle,
  'new-product': Sparkles,
  inbox: Inbox,
};

export function notificationIcon(
  icon: ProNotificationIcon,
): NotificationIconComponent {
  return ICONS[icon] ?? Inbox;
}

// ------------------------------------------------------------------
// Traduction
// ------------------------------------------------------------------

/**
 * Traduit une chaîne du descriptif. Une clé vide signifie « le serveur a écrit
 * ce texte lui-même » (type inconnu du client) : on le rend tel quel.
 */
export function translateL10n(entry: ProNotificationL10n, t: TFunction): string {
  if (!entry.key) return entry.fallback;
  return t(entry.key, entry.fallback, entry.vars ?? {}) as string;
}

/** Vue traduite + formatée, prête à peindre. */
export interface ProNotificationDisplay {
  view: ProNotificationView;
  title: string;
  body: string | null;
  palette: ProNotificationPalette;
  Icon: NotificationIconComponent;
  stats: Array<{ id: string; label: string; value: string; palette: ProNotificationPalette; trend?: 'up' | 'down' | 'flat' }>;
  amountLabel: string | null;
}

export function buildNotificationDisplay(
  notification: ProNotification,
  t: TFunction,
  locale: string,
): ProNotificationDisplay {
  const view = describeProNotification(notification);
  const palette = NOTIFICATION_PALETTE[view.tone];

  // Deux variables du corps ne peuvent pas être calculées par le domaine :
  //  - `period`, dont le libellé dépend de la langue active ;
  //  - `amount`, dont le repli domaine ne groupe pas les milliers (il n'a pas
  //    le droit d'importer `formatTND`, qui vit côté app).
  // On les écrase ici, sinon le corps affiche « 1642,500 DT » sous un titre
  // qui affiche « 1 642,500 DT ».
  const body = view.body
    ? translateL10n(withComputedVars(view.body, view, locale), t)
    : null;

  return {
    view,
    title: translateL10n(view.title, t),
    body,
    palette,
    Icon: notificationIcon(view.icon),
    stats: view.stats.map((stat) => ({
      id: stat.id,
      label: translateL10n(stat.label, t),
      value: formatStatValue(stat),
      palette: NOTIFICATION_PALETTE[stat.tone ?? 'neutral'],
      ...(stat.trend ? { trend: stat.trend } : {}),
    })),
    amountLabel: view.amount ? formatSignedAmount(view.amount) : null,
  };
}

/** Réinjecte dans le corps les variables que seule la couche app sait calculer. */
function withComputedVars(
  body: ProNotificationL10n,
  view: ProNotificationView,
  locale: string,
): ProNotificationL10n {
  const vars: Record<string, string | number> = { ...(body.vars ?? {}) };
  if (view.period) vars['period'] = formatPeriodLabel(view.period, locale);
  if (view.amount) vars['amount'] = formatTND(Math.abs(view.amount.value));
  return { ...body, vars };
}

function formatStatValue(stat: ProNotificationStat): string {
  switch (stat.kind) {
    case 'money':
      return formatTND(Number(stat.value));
    case 'count':
      return String(stat.value);
    case 'percent': {
      const n = Number(stat.value);
      return `${n > 0 ? '+' : ''}${n} %`;
    }
    default:
      return String(stat.value);
  }
}

function formatSignedAmount(amount: {
  value: number;
  direction: 'in' | 'out' | 'neutral';
}): string {
  const formatted = formatTND(Math.abs(amount.value));
  if (amount.direction === 'in') return `+${formatted}`;
  if (amount.direction === 'out') return `−${formatted}`;
  return formatted;
}

// ------------------------------------------------------------------
// Dates
// ------------------------------------------------------------------

/** « juillet 2026 » pour un mois, « 20 – 26 juil. » pour une semaine. */
export function formatPeriodLabel(
  period: ProNotificationPeriod,
  locale: string,
): string {
  const start = new Date(period.start);
  if (Number.isNaN(start.getTime())) return '';
  if (period.granularity === 'month') {
    return start.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  }
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const fmt: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  return `${start.toLocaleDateString(locale, fmt)} – ${end.toLocaleDateString(locale, fmt)}`;
}

const RELATIVE_STEPS: Array<[limit: number, unit: Intl.RelativeTimeFormatUnit, div: number]> = [
  [60, 'second', 1],
  [3600, 'minute', 60],
  [86_400, 'hour', 3600],
  [604_800, 'day', 86_400],
  [2_629_800, 'week', 604_800],
  [31_557_600, 'month', 2_629_800],
];

/**
 * « il y a 5 min », « منذ ٥ دقائق ». On passe par `Intl.RelativeTimeFormat`
 * plutôt que par des clés i18n : la pluralisation arabe est gérée par le
 * moteur, alors que nos clés n'ont pas de suffixes `_one`/`_other`.
 */
export function formatRelativeTime(iso: string, locale: string, now: Date): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const seconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const abs = Math.abs(seconds);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  // Moins d'une minute : « à l'instant » plutôt qu'un décompte de secondes.
  if (abs < 45) return rtf.format(0, 'second');

  for (const [limit, unit, div] of RELATIVE_STEPS) {
    if (abs < limit) return rtf.format(Math.round(seconds / div), unit);
  }
  return rtf.format(Math.round(seconds / 31_557_600), 'year');
}
